import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import {
    XCircle,
    CheckCircle,
    Calendar,
    Clock,
    Phone,
    User,
    Hash,
    MessageSquare,
    Send,
    AlertCircle,
    RefreshCw,
    PlusCircle,
    Loader2
} from 'lucide-react';
import { apiFetch } from '../api';
import { useWebSocket } from '../websocket/WebSocketContext';
import { ensureSocketConnection } from '../websocket/socketUtils';

// ── Renders directly on document.body — escapes all parent overflow/transform ──
function Portal({ children }) {
    return ReactDOM.createPortal(children, document.body);
}

export default function SMSDetails({ selectedSms, onClose, setError, loginId, deliveryMode }) {
    const { client } = useWebSocket();
    const [showErrorPayload, setShowErrorPayload] = useState(false);
    const [smsBodyResponse, setSmsBodyResponse] = useState(null);
    const [isBodyLoading, setIsBodyLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [actionSuccess, setActionSuccess] = useState('');
    const [actionError, setActionError] = useState('');

    // Lock body scroll while open
    useEffect(() => {
        if (!selectedSms) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [selectedSms]);

    // Close on Escape
    useEffect(() => {
        if (!selectedSms) return;
        const onKey = (e) => { if (e.key === 'Escape') { if (showErrorPayload) setShowErrorPayload(false); else onClose(); } };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [selectedSms, showErrorPayload, onClose]);

    const safeJson = async (res) => { try { return await res.json(); } catch { return null; } };

    const fetchSMSBodyById = async (smsId) => {
        if (!smsId) { setError('Missing sms id'); return; }
        setIsBodyLoading(true);
        setError('');
        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); }
                catch { setError('Socket connection failed.'); setIsBodyLoading(false); return; }

                const topic = `/topic/get/sms/body/response/${loginId}`;
                const sub = client.subscribe(topic, (msg) => {
                    try {
                        const raw = JSON.parse(msg.body);
                        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
                        data?.STATUS === 'SUCCESS' ? setSmsBodyResponse(data.DATA) : setError('SMS body not found.');
                    } catch { setError('Failed to parse SMS body data.'); }
                    finally { sub.unsubscribe(); setIsBodyLoading(false); }
                });
                client.publish({ destination: '/app/get/sms/body/response', body: smsId });
            } else {
                const res = await apiFetch(`/get/sms/body/response?smsId=${encodeURIComponent(smsId)}`);
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await safeJson(res);
                data?.STATUS === 'SUCCESS' ? setSmsBodyResponse(data.DATA) : setError('SMS body not found.');
                setIsBodyLoading(false);
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            setError('Failed to load SMS body.');
            setSmsBodyResponse(null);
            setIsBodyLoading(false);
        }
    };

    const handleResendSMS = async () => {
        setIsResending(true);
        setActionSuccess('');
        setActionError('');
        let payload;
        try {
            if (selectedSms.smsVendor === 'PAYTM') {
                const bulkSmsArr = smsBodyResponse?.bulkSms?.map((bulk) => ({
                    mobileNo: bulk?.smsData?.receiver || bulk?.mobileNo || bulk?.receiver,
                    smsTemplate: bulk?.smsData?.content || bulk?.smsTemplate || bulk?.content,
                    appName: selectedSms.appName,
                    templateId: bulk?.smsData?.template_id || bulk?.template_id || '',
                    smsData: bulk?.smsData || null
                })) || [];
                payload = { _id: selectedSms._id, appName: selectedSms.appName, smsVendor: 'PAYTM', sms_bulk_type: selectedSms.sms_bulk_type, bulkSms: bulkSmsArr };
            } else if (selectedSms.sms_bulk_type) {
                const bulkSmsArr = smsBodyResponse?.bulkSms?.map((bulk) => ({
                    mobileNo: bulk.mobileNo, smsTemplate: bulk.smsTemplate,
                    appName: bulk.appName || selectedSms.appName, smsData: bulk.smsData || null
                })) || [];
                payload = { _id: selectedSms._id, smsVendor: selectedSms.smsVendor, sms_bulk_type: true, appName: selectedSms.appName, bulkSms: bulkSmsArr };
            } else {
                payload = {
                    _id: selectedSms._id, smsVendor: selectedSms.smsVendor, sms_bulk_type: false, appName: selectedSms.appName,
                    sms: { mobileNo: smsBodyResponse?.sms?.mobileNo, smsTemplate: smsBodyResponse?.sms?.smsTemplate, appName: smsBodyResponse?.sms?.appName || selectedSms.appName, smsData: smsBodyResponse?.sms?.smsData || null }
                };
            }
        } catch (e) { setActionError('Failed to prepare SMS data.'); setIsResending(false); return; }

        try {
            if (deliveryMode === 'socket') {
                try { await ensureSocketConnection(client); }
                catch { setActionError('Socket connection failed.'); setIsResending(false); return; }
                const sub = client.subscribe(`/topic/sendSms/${loginId}`, (msg) => {
                    try {
                        const data = JSON.parse(msg.body);
                        if (data.STATUS === 'SUCCESS') { setActionSuccess('SMS resent successfully!'); setTimeout(() => setActionSuccess(''), 3000); }
                        else { setActionError(`Failed: ${data.MESSAGE || 'Unknown'}`); setTimeout(() => setActionError(''), 3000); }
                    } catch { setActionError('Failed to parse response.'); }
                    finally { sub.unsubscribe(); setIsResending(false); }
                });
                client.publish({ destination: '/app/sendSms', body: JSON.stringify(payload) });
            } else {
                const res = await apiFetch('/sendSms', { method: 'POST', body: JSON.stringify(payload) });
                const data = await res.json();
                if (data.STATUS === 'SUCCESS') { setActionSuccess('SMS resent successfully!'); setTimeout(() => setActionSuccess(''), 3000); }
                else { setActionError(`Failed: ${data.MESSAGE || 'Unknown'}`); setTimeout(() => setActionError(''), 3000); }
                setIsResending(false);
            }
        } catch (err) { setActionError('Network error while resending.'); setTimeout(() => setActionError(''), 3000); setIsResending(false); }
    };

    useEffect(() => { if (selectedSms) fetchSMSBodyById(selectedSms._id); }, [selectedSms]);

    if (!selectedSms) return null;

    // ── Helpers ──────────────────────────────────────────────────────────────
    const statusColor = (response, status) => {
        if (status === 'SUCCESS' || response?.toLowerCase().includes('successfully')) return 'text-green-400';
        if (status === 'FAILED' || response?.toLowerCase().includes('error') || response?.toLowerCase().includes('invalid')) return 'text-red-400';
        return 'text-yellow-400';
    };
    const StatusIcon = (response, status) => {
        if (status === 'SUCCESS' || response?.toLowerCase().includes('successfully')) return CheckCircle;
        if (status === 'FAILED' || response?.toLowerCase().includes('error') || response?.toLowerCase().includes('invalid')) return XCircle;
        return AlertCircle;
    };
    const fmtPhone = (p) => (!p || p.length !== 10) ? (p || '') : p.replace(/(\d{5})(\d{5})/, '$1 $2');
    const normalizeBulk = (arr) => {
        if (!Array.isArray(arr)) return [];
        return (arr.length > 0 && Array.isArray(arr[0])) ? arr.flat().filter(Boolean) : arr.filter(Boolean);
    };
    const extractMobiles = (xml) => {
        if (!xml) return [];
        const re = /TO="(\d+)"/g; let m; const out = [];
        while ((m = re.exec(xml)) !== null) out.push(m[1]);
        return out;
    };

    // ── Reusable label ────────────────────────────────────────────────────────
    const Label = ({ children }) => (
        <p className="text-white/40 text-[10px] uppercase tracking-widest mb-1.5 font-semibold">{children}</p>
    );

    // ── Info cell (icon + value) ──────────────────────────────────────────────
    const InfoCell = ({ label, icon: Ic, iconCls = 'text-white/50', value }) => (
        <div>
            <Label>{label}</Label>
            <div className="flex items-center gap-2">
                {Ic && <Ic className={`w-4 h-4 flex-shrink-0 ${iconCls}`} />}
                <span className="text-white text-sm font-mono break-all">{value || 'N/A'}</span>
            </div>
        </div>
    );

    const smsResponse = smsBodyResponse?.sms_response;
    const SIcon = StatusIcon(smsResponse, selectedSms.STATUS);
    const showResendButton = selectedSms.STATUS === 'FAILED';
    const showQueueButton = selectedSms.STATUS === 'PENDING';

    // ── Modal dimensions (same trick as EmailDetails) ─────────────────────────
    const modalStyle = {
        width: 'min(calc(100vw - 2rem), 780px)',
        maxHeight: 'calc(100vh - 2rem)',
        zIndex: 100000,
    };

    return (
        <Portal>
            {/* ── Main overlay ── */}
            <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 99999 }}>
                {/* Backdrop */}
                <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />

                {/* Modal card */}
                <div className="relative flex flex-col bg-slate-900 border border-white/15 rounded-2xl shadow-2xl overflow-hidden" style={modalStyle}>

                    {/* ── Header ── */}
                    <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-6 py-4 border-b border-white/10 bg-white/5">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
                                <MessageSquare className="w-4 h-4 text-white" />
                            </div>
                            <div className="min-w-0">
                                <h3 className="text-base sm:text-lg font-bold text-white">SMS Details</h3>
                                <p className="text-[10px] text-white/35 font-mono truncate hidden sm:block">{selectedSms._id}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                            {smsBodyResponse?.payload_error_list?.length > 0 && (
                                <button onClick={() => setShowErrorPayload(true)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 border border-red-500/30 text-red-200 rounded-lg hover:bg-red-500/30 transition-colors text-xs font-medium">
                                    <AlertCircle className="w-3.5 h-3.5" />Error Payload
                                </button>
                            )}
                            <button onClick={onClose} className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                                <XCircle className="w-4 h-4 text-white/70" />
                            </button>
                        </div>
                    </div>

                    {/* ── Scrollable body ── */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain">
                        <div className="px-5 sm:px-6 py-5 space-y-5">

                            {/* Action feedback */}
                            {(actionSuccess || actionError) && (
                                <div className={`flex items-center gap-2 p-3 rounded-xl border text-sm ${actionSuccess ? 'bg-green-500/15 border-green-500/30 text-green-200' : 'bg-red-500/15 border-red-500/30 text-red-200'}`}>
                                    {actionSuccess ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                                    <span>{actionSuccess || actionError}</span>
                                </div>
                            )}

                            {/* Action buttons (Resend / Queue) */}
                            {(showResendButton || showQueueButton) && (
                                <div className="flex gap-3">
                                    {showResendButton && (
                                        <button onClick={handleResendSMS} disabled={isResending}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white rounded-xl font-semibold transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                                            {isResending ? <><Loader2 className="w-4 h-4 animate-spin" />Resending…</> : <><RefreshCw className="w-4 h-4" />Resend SMS</>}
                                        </button>
                                    )}
                                    {showQueueButton && (
                                        <button onClick={handleResendSMS} disabled={isResending}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 text-white rounded-xl font-semibold transition-all text-sm disabled:opacity-50 disabled:cursor-not-allowed">
                                            {isResending ? <><Loader2 className="w-4 h-4 animate-spin" />Adding…</> : <><PlusCircle className="w-4 h-4" />Add to Queue</>}
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Basic info grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {selectedSms?.rf1_id && <InfoCell label="Site ID" value={selectedSms.rf1_id} />}
                                {selectedSms?.rf1_name && <InfoCell label="Site Name" value={selectedSms.rf1_name} />}
                                {selectedSms?.rf2_id && <InfoCell label="Location ID" value={selectedSms.rf2_id} />}
                                {selectedSms?.rf2_name && <InfoCell label="Location Name" value={selectedSms.rf2_name} />}
                                <div>
                                    <Label>ID</Label>
                                    <p className="text-white/80 text-xs font-mono bg-white/5 border border-white/10 px-3 py-2 rounded-lg break-all">{selectedSms._id}</p>
                                </div>
                                {selectedSms?.ref_id && <InfoCell label="Reference ID" value={selectedSms.ref_id} />}
                                <div>
                                    <Label>Status</Label>
                                    <div className="flex items-center gap-2">
                                        <SIcon className={`w-4 h-4 ${statusColor(smsResponse, selectedSms.STATUS)}`} />
                                        <span className={`text-sm font-semibold ${statusColor(smsResponse, selectedSms.STATUS)}`}>{selectedSms.STATUS}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Type + Vendor */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label>Type</Label>
                                    <div className="flex items-center gap-2">
                                        {selectedSms.sms_bulk_type
                                            ? <><Send className="w-4 h-4 text-blue-400" /><span className="text-white text-sm">Bulk SMS</span><span className="px-2 py-0.5 bg-blue-500/20 text-blue-200 text-xs rounded-full">BULK</span></>
                                            : <><MessageSquare className="w-4 h-4 text-green-400" /><span className="text-white text-sm">Single SMS</span></>
                                        }
                                    </div>
                                </div>
                                <InfoCell label="SMS Vendor" value={selectedSms.smsVendor} />
                            </div>

                            {/* SMS Response */}
                            {smsBodyResponse?.sms_response && (
                                <div>
                                    <Label>SMS Response</Label>
                                    <div className="bg-white/5 border border-white/10 px-4 py-3 rounded-xl">
                                        <p className="text-white font-mono text-sm break-all">{smsBodyResponse.sms_response}</p>
                                    </div>
                                </div>
                            )}

                            {/* Loading state */}
                            {isBodyLoading && (
                                <div className="flex items-center justify-center gap-3 py-8">
                                    <Loader2 className="w-5 h-5 animate-spin text-purple-400" />
                                    <span className="text-white/50 text-sm">Loading SMS details…</span>
                                </div>
                            )}

                            {/* ── PAYTM Bulk SMS Details ── */}
                            {!isBodyLoading && selectedSms.smsVendor === 'PAYTM' && smsBodyResponse?.bulkSms?.length > 0 && (
                                <div>
                                    <Label>Bulk SMS Details (PAYTM)</Label>
                                    <div className="space-y-3">
                                        {smsBodyResponse.bulkSms.map((bulk, i) => (
                                            <div key={i} className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20 rounded-xl p-4 space-y-4">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-7 h-7 bg-purple-500/20 rounded-full flex items-center justify-center">
                                                        <span className="text-purple-300 font-bold text-xs">P</span>
                                                    </div>
                                                    <span className="text-purple-300 font-semibold text-sm">PAYTM SMS #{i + 1}</span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-3">
                                                    <InfoCell label="Location ID" icon={User} iconCls="text-purple-400" value={bulk?.smsData?.rf2_id || bulk?.rf2_id} />
                                                    <InfoCell label="Location Name" icon={User} iconCls="text-purple-400" value={bulk?.smsData?.rf2_name || bulk?.rf2_name} />
                                                    <InfoCell label="Sender ID" icon={User} iconCls="text-purple-400" value={bulk?.smsData?.sender || bulk?.sender} />
                                                    <InfoCell label="Receiver" icon={Phone} iconCls="text-purple-400" value={fmtPhone(bulk.smsData?.receiver || bulk?.mobileNo || bulk?.receiver)} />
                                                    <InfoCell label="Message Type" icon={MessageSquare} iconCls="text-purple-400" value={bulk.smsData?.msg_type || bulk?.msg_type || 'TEXT'} />
                                                    <InfoCell label="App Name" icon={Hash} iconCls="text-purple-400" value={selectedSms.appName} />
                                                    {(bulk?.smsData?.pe_id || bulk?.pe_id) && <InfoCell label="PE ID" value={bulk?.smsData?.pe_id || bulk?.pe_id} />}
                                                    {(bulk?.smsData?.template_id || bulk?.template_id) && <InfoCell label="Template ID" value={bulk?.smsData?.template_id || bulk?.template_id} />}
                                                    {(bulk?.smsData?.ref_id || bulk?.ref_id) && <InfoCell label="Reference ID" value={bulk?.smsData?.ref_id || bulk?.ref_id} />}
                                                    {(bulk?.smsData?.dlt_chain || bulk?.dlt_chain) && <InfoCell label="DLT Chain" value={bulk?.smsData?.dlt_chain || bulk?.dlt_chain} />}
                                                </div>
                                                <div>
                                                    <Label>Message Content</Label>
                                                    <div className="bg-white/10 border border-purple-500/20 px-3 py-2.5 rounded-lg">
                                                        <p className="text-white text-sm whitespace-pre-wrap">{bulk.smsData?.content || bulk?.smsTemplate || bulk?.content || 'N/A'}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* PAYTM response */}
                                    {smsBodyResponse?.bulk_sms_response?.length > 0 && (
                                        <div className="mt-4">
                                            <Label>PAYTM Response</Label>
                                            <div className="space-y-2">
                                                {smsBodyResponse.bulk_sms_response.map((resp, i) => {
                                                    const items = Array.isArray(resp) ? resp : [resp];
                                                    return items.map((item, j) => {
                                                        let parsed = {};
                                                        try { parsed = typeof item?.Response === 'string' ? JSON.parse(item.Response) : (item?.Response || item || {}); } catch { parsed = { raw: item?.Response }; }
                                                        return (
                                                            <div key={`${i}-${j}`} className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /><span className="text-green-300 font-medium text-sm">Delivery Status</span></div>
                                                                    {item?.status && <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${item.status === 'SUCCESS' ? 'bg-green-500/20 text-green-200' : 'bg-red-500/20 text-red-200'}`}>{item.status}</span>}
                                                                </div>
                                                                <div className="grid grid-cols-2 gap-2 text-xs">
                                                                    {parsed.status && <div><span className="text-white/40">Status Code: </span><span className="text-white font-mono">{parsed.status}</span></div>}
                                                                    {parsed.RESPONSE && <div><span className="text-white/40">Response: </span><span className="text-white">{parsed.RESPONSE}</span></div>}
                                                                    {parsed.DESCRIPTION && <div className="col-span-2"><span className="text-white/40">Description: </span><span className="text-white">{parsed.DESCRIPTION}</span></div>}
                                                                    {parsed.MESSAGE_ID && <div className="col-span-2"><span className="text-white/40">Message ID: </span><span className="text-white font-mono break-all">{parsed.MESSAGE_ID}</span></div>}
                                                                    {parsed.TIME && <div><span className="text-white/40">Time: </span><span className="text-white">{parsed.TIME}</span></div>}
                                                                    {parsed.raw && <div><span className="text-white/40">Raw: </span><span className="text-white">{parsed.raw}</span></div>}
                                                                </div>
                                                            </div>
                                                        );
                                                    });
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Non-PAYTM Bulk SMS Details ── */}
                            {!isBodyLoading && selectedSms.smsVendor !== 'PAYTM' && selectedSms.sms_bulk_type && (
                                <div>
                                    <Label>Bulk SMS Details</Label>
                                    <div className="space-y-3">
                                        {/* bulkSms array */}
                                        {smsBodyResponse?.bulkSms?.length > 0 && smsBodyResponse.bulkSms.map((bulk, i) => (
                                            <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <InfoCell label="Location ID" icon={User} iconCls="text-purple-400" value={bulk?.smsData?.rf2_id || bulk?.rf2_id} />
                                                    <InfoCell label="Location Name" icon={User} iconCls="text-purple-400" value={bulk?.smsData?.rf1_name || bulk?.rf2_name} />
                                                    <InfoCell label="Mobile Number" icon={Phone} value={fmtPhone(bulk.mobileNo)} />
                                                    <InfoCell label="App Name" icon={User} value={bulk?.appName || selectedSms.appName} />
                                                </div>
                                                <div>
                                                    <Label>SMS Template</Label>
                                                    <div className="bg-white/10 border border-white/10 px-3 py-2.5 rounded-lg">
                                                        <p className="text-white text-sm whitespace-pre-wrap">{bulk.smsTemplate}</p>
                                                    </div>
                                                </div>
                                                {bulk.smsData && Object.keys(bulk.smsData).length > 0 && (
                                                    <div>
                                                        <Label>SMS Data</Label>
                                                        <div className="bg-white/10 border border-white/10 px-3 py-2.5 rounded-lg max-h-32 overflow-y-auto">
                                                            <pre className="text-white text-xs overflow-x-auto">{JSON.stringify(bulk.smsData, null, 2)}</pre>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}

                                        {/* bulk_template_sms fallback */}
                                        {!selectedSms?.bulkSms && smsBodyResponse?.bulk_template_sms && (
                                            <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <InfoCell label="Mobile Numbers" icon={Phone} value={extractMobiles(smsBodyResponse.bulk_template_sms).join(', ')} />
                                                    <InfoCell label="App Name" icon={User} value={selectedSms.appName} />
                                                </div>
                                                <div>
                                                    <Label>SMS Template</Label>
                                                    <div className="bg-white/10 border border-white/10 px-3 py-2.5 rounded-lg">
                                                        <p className="text-white text-sm whitespace-pre-wrap">{smsBodyResponse.bulk_template_sms}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Bulk SMS response */}
                                    {smsBodyResponse?.bulk_sms_response?.length > 0 && (
                                        <div className="mt-3">
                                            <Label>Bulk SMS Response</Label>
                                            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
                                                <div className="flex flex-wrap gap-2">
                                                    {normalizeBulk(smsBodyResponse.bulk_sms_response).map((resp, i) => (
                                                        <div key={resp?.MID ?? resp?.mobile_no ?? i} className="px-3 py-2 bg-green-500/15 border border-green-500/20 text-green-200 rounded-lg text-xs font-mono space-y-0.5">
                                                            {Object.entries(resp).map(([k, v]) => (
                                                                <div key={k}><strong className="text-white/50">{k}:</strong> {String(v)}</div>
                                                            ))}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* ── Single SMS Details ── */}
                            {!isBodyLoading && !selectedSms.sms_bulk_type && smsBodyResponse?.sms && (
                                <div>
                                    <Label>SMS Details</Label>
                                    <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
                                        <div className="grid grid-cols-2 gap-3">
                                            <InfoCell label="Location ID" icon={User} iconCls="text-purple-400" value={smsBodyResponse.sms.rf2_id} />
                                            <InfoCell label="Location Name" icon={User} iconCls="text-purple-400" value={smsBodyResponse.sms.rf2_name} />
                                            <InfoCell label="Mobile Number" icon={Phone} value={fmtPhone(smsBodyResponse.sms.mobileNo)} />
                                            <InfoCell label="App Name" icon={User} value={smsBodyResponse.sms.appName} />
                                        </div>
                                        <div>
                                            <Label>SMS Template</Label>
                                            <div className="bg-white/10 border border-white/10 px-3 py-2.5 rounded-lg">
                                                <p className="text-white text-sm whitespace-pre-wrap">{smsBodyResponse.sms.smsTemplate}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Timestamps */}
                            <div>
                                <Label>Timestamp Information</Label>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {[['Date', Calendar, 'text-blue-400', selectedSms.date], ['Creation Time', Clock, 'text-green-400', selectedSms.creation_time_str], ['Timestamp (ms)', Clock, 'text-purple-400', selectedSms.creation_time_ms]].map(([lbl, Ic, cls, val]) => (
                                        <div key={lbl} className="bg-white/5 border border-white/10 rounded-xl p-3">
                                            <div className="flex items-center gap-1.5 mb-1.5"><Ic className={`w-3.5 h-3.5 ${cls}`} /><p className="text-white/40 text-xs">{lbl}</p></div>
                                            <p className="text-white text-xs font-mono">{val || 'N/A'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="h-1" />
                        </div>
                    </div>

                    {/* ── Footer ── */}
                    <div className="flex-shrink-0 flex items-center justify-end gap-2 px-5 sm:px-6 py-4 border-t border-white/10 bg-slate-900/80">
                        <button onClick={onClose} className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm font-medium transition-colors">
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Error Payload Modal (layered above main modal) ── */}
            {showErrorPayload && smsBodyResponse?.payload_error_list && (
                <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 100001 }}>
                    <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowErrorPayload(false)} />
                    <div className="relative flex flex-col bg-slate-900 border border-red-500/30 rounded-2xl shadow-2xl overflow-hidden"
                        style={{ width: 'min(calc(100vw - 2rem), 780px)', maxHeight: 'calc(100vh - 2rem)', zIndex: 100002 }}>
                        <div className="flex-shrink-0 flex items-center justify-between px-5 sm:px-6 py-4 border-b border-red-500/20 bg-red-500/5">
                            <div className="flex items-center gap-2">
                                <AlertCircle className="w-5 h-5 text-red-400" />
                                <h3 className="text-base font-bold text-white">Error Payload Details</h3>
                            </div>
                            <button onClick={() => setShowErrorPayload(false)} className="w-8 h-8 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors">
                                <XCircle className="w-4 h-4 text-white/70" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto overscroll-contain">
                            <div className="px-5 sm:px-6 py-5 space-y-4">
                                {smsBodyResponse.payload_error_list.map((err, i) => (
                                    <div key={i} className="bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/20 rounded-xl p-4 space-y-3">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 bg-red-500/20 rounded-full flex items-center justify-center">
                                                <span className="text-red-300 font-bold text-xs">{i + 1}</span>
                                            </div>
                                            <span className="text-red-300 font-semibold text-sm">Error #{i + 1}</span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            {err?.ref_id && <InfoCell label="Reference ID" value={err.ref_id} />}
                                            {err?.error_reason && <div><Label>Error Reason</Label><p className="text-red-200 text-sm font-semibold">{err.error_reason}</p></div>}
                                            {err?.receiver && <InfoCell label="Receiver" icon={Phone} iconCls="text-red-400" value={fmtPhone(err.receiver)} />}
                                            {err?.sender && <InfoCell label="Sender" icon={User} iconCls="text-red-400" value={err.sender} />}
                                            {err?.msz_type && <InfoCell label="Message Type" icon={MessageSquare} iconCls="text-red-400" value={err.msz_type} />}
                                            {err?.template_id && <InfoCell label="Template ID" value={err.template_id} />}
                                            {err?.pe_id && <InfoCell label="PE ID" value={err.pe_id} />}
                                            {err?.dlt_chain && <InfoCell label="DLT Chain" value={err.dlt_chain} />}
                                            {err?.tag_name && <InfoCell label="Tag Name" value={err.tag_name} />}
                                        </div>
                                        {err?.content && (
                                            <div>
                                                <Label>Message Content</Label>
                                                <div className="bg-white/10 border border-red-500/20 px-3 py-2.5 rounded-lg">
                                                    <p className="text-white text-sm whitespace-pre-wrap">{err.content}</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex-shrink-0 flex justify-end px-5 py-3 border-t border-white/10 bg-slate-900/80">
                            <button onClick={() => setShowErrorPayload(false)} className="px-4 py-2 bg-white/10 hover:bg-white/15 text-white rounded-xl text-sm font-medium transition-colors">Close</button>
                        </div>
                    </div>
                </div>
            )}
        </Portal>
    );
}